import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MonthRangePicker } from "../components/datepicker/MonthRangePicker";
import { MonthDate } from "../components/datepicker/types";
import { getMonthDateRangeFromCurrentDate } from "../helpers/comparableFilters.helpers";
import { useQuery } from "react-query";
import { getAppraiserPropertiesStatistics } from "../api/api";
import { CircularProgress, MenuItem, Select } from "@mui/material";
import { formatPrice, getMonthLabel } from "common/helpers/text.helpers";
import { BarChart } from "@mui/x-charts";
import colors from "common/styles/colors";

import { SimpleTableComponent } from "../components/tables/simpleTable/SimpleTableComponent";
import { SimpleTableRow } from "../components/tables/simpleTable/SimpleTableRow";
import { SimpleTableHeader } from "../components/tables/simpleTable/SimpleTableHeader";
import { PropertyStatistics, ValuationTypeFilter } from "../types/api.types";
import { CenteredContentWrapper } from "../components/wrappers/CenteredContentWrapper";
import { SimpleTableElement } from "../components/tables/simpleTable/SimpleTableElement";
import {
  getLithuanianDateString,
  getLithuanianDateTimeString,
} from "common/helpers/dateStringHelpers";
import { DesignationType } from "common/types/comparableFields.types";
import { getDesignationTypeTranslation } from "../helpers/comparableFields.helpers";
import { AverageExecutionTime } from "../components/statistics/AverageExecutionTime";
import { getValuationTypeLabel } from "../helpers/valuationType.helpers";
import { ValuationType } from "common/types/common.types";
import { ValuationTypeFilter as ValuationTypeFilterComponent } from "../components/filters/ValuationTypeFilter";

type DateFilterType = "createdAt" | "evaluatedAt" | "evaluationStarted";

interface ValuationsStatisticsProps {}

// Get field names directly from the PropertyStatistics interface
const propertyStatisticsFields: (keyof PropertyStatistics)[] = [
  "createdAt",
  "evaluationStarted",
  "evaluatedAt",
  "designationType",
  "valuationType",
  "address",
  "fullPrice",
  "totalPrice",
  "discountPercentage",
  "discountCode",
  "appraiser",
  "customer",
  "deliveryDate",
  "executionDays",
  "executionWorkingDays",
];

export const ValuationsStatistics: React.FC<ValuationsStatisticsProps> = () => {
  const { t } = useTranslation();

  const fieldParser = (
    field: keyof PropertyStatistics,
    property: PropertyStatistics
  ) => {
    if (property[field] == null) {
      return "";
    }
    if (
      field === "createdAt" ||
      field === "evaluatedAt" ||
      field === "deliveryDate" ||
      field === "evaluationStarted"
    ) {
      return getLithuanianDateTimeString(property[field] ?? "");
    }
    if (field === "designationType") {
      return getDesignationTypeTranslation(
        property[field] ?? DesignationType.Residential1Flat
      );
    }
    if (field === "discountPercentage") {
      return `${property[field]}%`;
    }
    if (field === "fullPrice" || field === "totalPrice") {
      return formatPrice(property[field]);
    }
    if (field === "executionDays" || field === "executionWorkingDays") {
      return `${property[field]} ${t("valuationsStatistics.days")}`;
    }
    if (field === "valuationType") {
      return getValuationTypeLabel(property[field], t);
    }

    return property[field];
  };

  const getFieldWidth = (field: keyof PropertyStatistics) => {
    if (field === "address") {
      return "250px";
    }
    if (field === "designationType") {
      return "200px";
    }
    if (
      field === "createdAt" ||
      field === "evaluatedAt" ||
      field === "deliveryDate"
    ) {
      return "100px";
    }
    if (field === "discountPercentage") {
      return "100px";
    }
    if (field === "fullPrice" || field === "totalPrice") {
      return "80px";
    }
    if (field === "appraiser" || field === "customer") {
      return "250px";
    }
    if (field === "executionDays") {
      return "80px";
    }

    return "100px";
  };

  const [selectedDates, setSelectedDates] = useState(() => {
    const monthDate = getMonthDateRangeFromCurrentDate(3);

    return {
      start: monthDate.startDate,
      end: monthDate.endDate,
    };
  });

  const [dateFilterType, setDateFilterType] =
    useState<DateFilterType>("createdAt");

  const [valuationType, setValuationType] =
    useState<ValuationTypeFilter>("all");

  const { data, isLoading } = useQuery(
    [
      "appraiserPropertiesStatistics",
      selectedDates,
      dateFilterType,
      valuationType,
    ],
    () =>
      getAppraiserPropertiesStatistics(
        selectedDates.start.month,
        selectedDates.start.year,
        selectedDates.end.month,
        selectedDates.end.year,
        dateFilterType,
        valuationType
      )
  );

  const dataByMonth = React.useMemo(() => {
    if (!data) return [];

    const dict_data: Record<string, number> = {};

    for (const property of data) {
      const dateValue = (property as any)[dateFilterType];
      if (!dateValue) continue;
      // Use UTC timezone for consistency (we also use UTC in the backend)
      const date = new Date(dateValue);
      const key = getMonthLabel(date.getUTCMonth() + 1, date.getUTCFullYear());
      dict_data[key] = dict_data[key] ? dict_data[key] + 1 : 1;
    }

    return Object.entries(dict_data).map(([key, value]) => ({
      month: key,
      count: value,
    }));
  }, [data, dateFilterType]);

  const cellSpacing = "15px";

  return (
    <div
      style={{
        backgroundColor: "white",
        padding: "20px",
        borderRadius: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "20px",
          marginBottom: "20px",
        }}
      >
        <div style={{ maxWidth: "400px" }}>
          <div style={{ marginBottom: "15px" }}>
            {t("valuationsStatistics.dateFilterType")}
          </div>
          <Select
            value={dateFilterType}
            onChange={(e) =>
              setDateFilterType(e.target.value as DateFilterType)
            }
            style={{ width: "300px" }}
          >
            <MenuItem value="createdAt">
              {t("valuationsStatistics.fields.createdAt")}
            </MenuItem>
            <MenuItem value="evaluationStarted">
              {t("valuationsStatistics.fields.evaluationStarted")}
            </MenuItem>
            <MenuItem value="evaluatedAt">
              {t("valuationsStatistics.fields.evaluatedAt")}
            </MenuItem>
          </Select>
        </div>

        <ValuationTypeFilterComponent
          value={valuationType}
          onChange={setValuationType}
        />
      </div>

      <AverageExecutionTime
        dateFilterType={dateFilterType}
        valuationType={valuationType}
      />

      <div
        style={{
          maxWidth: "400px",
          marginBottom: "20px",
        }}
      >
        <div style={{ marginBottom: "15px" }}>
          {t("valuationsStatistics.reportingPeriod")}
        </div>
        <MonthRangePicker
          selectedDatesInFilter={selectedDates}
          onDatesSelect={(start, end) => setSelectedDates({ start, end })}
          customPresets={[3, 6, 12]}
        />
      </div>
      {isLoading && (
        <CenteredContentWrapper>
          <CircularProgress />
        </CenteredContentWrapper>
      )}
      {!!dataByMonth && (
        <BarChart
          series={[
            {
              data: dataByMonth.map((d) => d.count),
              color: colors.purple2,
            },
          ]}
          xAxis={[
            {
              id: "months",
              scaleType: "band",
              data: dataByMonth.map((d) => d.month),
            },
          ]}
          height={300}
        />
      )}
      {!!data && (
        <div style={{ overflowX: "scroll" }}>
          <SimpleTableComponent>
            <SimpleTableRow>
              {propertyStatisticsFields.map((field, index) => (
                <SimpleTableHeader
                  key={field}
                  width={getFieldWidth(field)}
                  isFirstElement={index === 0}
                  forceWidth
                >
                  {t(`valuationsStatistics.fields.${field}`)}
                </SimpleTableHeader>
              ))}
            </SimpleTableRow>
            {data.map((property) => (
              <SimpleTableRow key={property.id}>
                {propertyStatisticsFields.map((field, index) => (
                  <SimpleTableElement
                    key={field}
                    style={{
                      paddingRight: cellSpacing,
                    }}
                  >
                    {fieldParser(field, property)}
                  </SimpleTableElement>
                ))}
              </SimpleTableRow>
            ))}
          </SimpleTableComponent>
        </div>
      )}
    </div>
  );
};
